import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  ArrowUp,
  ArrowDown,
  MapPin,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import AdminNav from "@/components/AdminNav";
import { api } from "@/lib/api";
import { authHeaders, getToken } from "@/lib/auth";
import { DIVISIONS } from "@/data/divisions";

const blankListing = {
  division: "real-estate",
  title: "",
  location: "",
  price: "",
  phone: "9354859598",
  tag: "",
  flyer: "",
  highlights: "",
  order: 0,
};

const AdminListings = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | listing object
  const [form, setForm] = useState(blankListing);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterDivision, setFilterDivision] = useState("real-estate");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/listings");
      setListings(data || []);
    } catch {
      toast.error("Failed to load listings.");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm({
      ...blankListing,
      division: filterDivision,
      order: (listings.filter((l) => l.division === filterDivision).length || 0) + 1,
    });
    setEditing("new");
  };

  const openEdit = (l) => {
    setForm({
      ...l,
      highlights: (l.highlights || []).join("\n"),
    });
    setEditing(l);
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(blankListing);
  };

  const save = async () => {
    if (!form.title || !form.division) {
      toast.error("Title and division are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      highlights: (form.highlights || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      if (editing === "new") {
        await api.post("/admin/listings", payload, { headers: authHeaders() });
        toast.success("Listing added.");
      } else {
        await api.put(`/admin/listings/${editing.id}`, payload, { headers: authHeaders() });
        toast.success("Listing updated.");
      }
      closeEditor();
      fetchAll();
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/listings/${confirmDelete.id}`, { headers: authHeaders() });
      toast.success("Listing deleted.");
      setConfirmDelete(null);
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const moveOrder = async (l, dir) => {
    const sameDivision = listings.filter((x) => x.division === l.division).sort((a, b) => a.order - b.order);
    const idx = sameDivision.findIndex((x) => x.id === l.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sameDivision.length) return;
    const other = sameDivision[swapIdx];
    try {
      await Promise.all([
        api.put(`/admin/listings/${l.id}`, { ...l, order: other.order }, { headers: authHeaders() }),
        api.put(`/admin/listings/${other.id}`, { ...other, order: l.order }, { headers: authHeaders() }),
      ]);
      fetchAll();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  const filtered = listings
    .filter((l) => (filterDivision === "all" ? true : l.division === filterDivision))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="admin-listings-page" className="min-h-screen bg-[#F8FAFC]">
      <AdminNav />
      <div className="px-6 md:px-12 lg:px-24 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
              Manage Listings
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mt-3">
              Featured Properties
            </h1>
            <p className="text-slate-600 mt-3 text-sm max-w-2xl">
              Add, edit, reorder or remove property listings shown on the Real Estate
              division page. Changes appear live on the website immediately.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={filterDivision} onValueChange={setFilterDivision}>
              <SelectTrigger className="w-44 h-11 rounded-full border-slate-300 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions</SelectItem>
                {DIVISIONS.map((d) => (
                  <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="add-listing-btn"
              onClick={openNew}
              className="rounded-full h-11 bg-[#0A2540] hover:bg-[#0F172A] text-white px-5"
            >
              <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add Listing
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-20">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-slate-500 py-20 rounded-3xl bg-white border border-slate-100">
            No listings yet. Click <strong>Add Listing</strong> to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((l, idx) => (
              <div
                key={l.id}
                data-testid={`listing-row-${l.id}`}
                className="rounded-2xl bg-white border border-slate-100 p-5 flex flex-col md:flex-row gap-5 hover:border-slate-200 transition-colors"
              >
                <div className="w-full md:w-48 h-32 md:h-32 flex-shrink-0 rounded-xl bg-slate-100 overflow-hidden">
                  {l.flyer ? (
                    <img src={l.flyer} alt={l.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">No image</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 flex-wrap">
                    <h3 className="font-heading text-lg font-medium text-slate-900 truncate">{l.title}</h3>
                    {l.tag && (
                      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A2540] bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                        {l.tag}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                    {l.location && <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{l.location}</span>}
                    {l.price && <span><span className="text-slate-400">Price: </span>{l.price}</span>}
                    {l.phone && <span><span className="text-slate-400">Phone: </span>{l.phone}</span>}
                    <span className="text-slate-400">Order: {l.order}</span>
                  </div>
                  {l.highlights && l.highlights.length > 0 && (
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2">
                      {l.highlights.slice(0, 3).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 self-start md:self-center flex-wrap">
                  <button
                    data-testid={`listing-up-${l.id}`}
                    onClick={() => moveOrder(l, -1)}
                    disabled={idx === 0}
                    className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    title="Move up"
                  >
                    <ArrowUp size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`listing-down-${l.id}`}
                    onClick={() => moveOrder(l, 1)}
                    disabled={idx === filtered.length - 1}
                    className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    title="Move down"
                  >
                    <ArrowDown size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`listing-edit-${l.id}`}
                    onClick={() => openEdit(l)}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-slate-200 hover:bg-slate-50 text-sm"
                  >
                    <Pencil size={13} strokeWidth={1.5} /> Edit
                  </button>
                  <button
                    data-testid={`listing-delete-${l.id}`}
                    onClick={() => setConfirmDelete(l)}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                  >
                    <Trash2 size={13} strokeWidth={1.5} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing === "new" ? "Add Listing" : "Edit Listing"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <Field label="Division *">
              <Select value={form.division} onValueChange={(v) => setForm({ ...form, division: v })}>
                <SelectTrigger className="h-11 rounded-xl border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map((d) => (
                    <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Display Order">
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="h-11 rounded-xl border-slate-200"
              />
            </Field>
            <Field label="Title *" full>
              <Input
                data-testid="listing-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Hermitage Royale — Fully Furnished Sample Flat"
                className="h-11 rounded-xl border-slate-200"
              />
            </Field>
            <Field label="Location">
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="VIP Road, Zirakpur"
                className="h-11 rounded-xl border-slate-200"
              />
            </Field>
            <Field label="Asking Price">
              <Input
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="₹3.30 Cr* or On Request"
                className="h-11 rounded-xl border-slate-200"
              />
            </Field>
            <Field label="Phone (for Call button)">
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="9354859598"
                className="h-11 rounded-xl border-slate-200"
              />
            </Field>
            <Field label="Tag / Badge">
              <Input
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                placeholder="3 BHK Premium"
                className="h-11 rounded-xl border-slate-200"
              />
            </Field>
            <Field label="Image URL (flyer)" full>
              <Input
                data-testid="listing-form-flyer"
                value={form.flyer}
                onChange={(e) => setForm({ ...form, flyer: e.target.value })}
                placeholder="https://... (see help below)"
                className="h-11 rounded-xl border-slate-200"
              />
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Tip: To get a public image URL, upload your photo to{" "}
                <a href="https://imgbb.com" target="_blank" rel="noreferrer" className="text-[#0284C7] underline">imgbb.com</a>{" "}
                (free, no signup needed) or{" "}
                <a href="https://postimages.org" target="_blank" rel="noreferrer" className="text-[#0284C7] underline">postimages.org</a>,
                then copy the direct image link and paste here.
              </p>
              {form.flyer && (
                <div className="mt-3 w-full max-w-[220px] aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 border border-slate-100">
                  <img src={form.flyer} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </Field>
            <Field label="Highlights (one per line)" full>
              <Textarea
                data-testid="listing-form-highlights"
                value={form.highlights}
                onChange={(e) => setForm({ ...form, highlights: e.target.value })}
                placeholder={"Ready to Move In\nSprawling Layout & Premium Finishes\nSerene Locality of Sector 45"}
                className="min-h-[120px] rounded-xl border-slate-200"
              />
            </Field>
          </div>
          <DialogFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={closeEditor} className="rounded-full">
              <X size={14} strokeWidth={1.5} className="mr-1" /> Cancel
            </Button>
            <Button
              data-testid="listing-form-save"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white"
            >
              <Save size={14} strokeWidth={1.5} className="mr-1" />
              {saving ? "Saving..." : "Save Listing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this listing?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. &quot;{confirmDelete?.title}&quot; will be removed from the website immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="listing-confirm-delete" onClick={doDelete} className="bg-red-600 hover:bg-red-700">
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Field = ({ label, children, full = false }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-2">
      {label}
    </label>
    {children}
  </div>
);

export default AdminListings;
